"use client";

import SectionLayout from "@/components/ui/SectionLayout";
import React, { useEffect } from "react";
import { CaseSectionProps } from "../model";
import { getDefaultCmsSection } from "@/utils/helper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faChevronDown,
  faFile,
  faHandHoldingDollar,
  faHome,
} from "@fortawesome/sharp-light-svg-icons";
import SomeSharingLinks from "@/components/ui/SomeSharingLinks";
import { usePathname } from "next/navigation";
import PropertyFacts from "@/components/ui/PropertyFacts";
import Card from "@/components/ui/Card";
import { motion } from "motion/react";
import Link from "next/link";
import RippleCtaButton from "@/components/ui/RippleCtaButton";

const CasePresentationSection: React.FC<
  CaseSectionProps<{
    card: { bgColor: string; textColor: string };
    somelinks: string[];
    someStyle: "square" | "default";
    assesmentFormSlug?: string;
    hideAssessmentFormButton?: boolean;
  }>
> = ({ cmsSection, caseData, pageCtx }) => {
  const pathname = usePathname();
  const fullUrl = `${pageCtx.baseUrl}${pathname}`;
  const encodedFullUrl = encodeURIComponent(fullUrl);

  const [collapsed, setCollapsed] = React.useState(true);
  const [showExpand, setShowExpand] = React.useState(true);
  const [height, setHeight] = React.useState(0);

  useEffect(() => {
    const contentSection = document.getElementById("contentSection");
    let totalHeight = 0;
    if (contentSection) {
      Array.from(contentSection.children).forEach((child) => {
        const childElement = child as HTMLElement;
        const style = window.getComputedStyle(childElement);
        const marginTop = parseFloat(style.marginTop);
        const marginBottom = parseFloat(style.marginBottom);
        totalHeight += childElement.clientHeight + marginTop + marginBottom;
      });
    }
    if (totalHeight < document.getElementById("menuSection")!.clientHeight) {
      setShowExpand(false);
    }
    setHeight(document.getElementById("menuSection")?.clientHeight ?? 0);
  }, []);
  return (
    <SectionLayout cmsSection={getDefaultCmsSection(cmsSection)}>
      <div className="flex flex-col gap-4">
        <div className="flex gap-4 flex-col md:flex-row justify-between">
          <div className="gap-4 flex flex-1 flex-col">
            <div className=" flex flex-1 flex-col">
              <div className="text-header-level-3">
                {caseData.advertisementAddressOrDisplayName}
              </div>
              <div className="homepage-text-lg">
                {caseData.marketingTexts?.headline}
              </div>
            </div>
            <motion.div
              data-collapsed={collapsed}
              animate={{
                height: collapsed ? "100px" : "auto",
              }}
              className="flex-col flex md:hidden h-[100px] relative overflow-hidden"
            >
              <motion.div
                animate={{
                  height: collapsed ? "100px" : "auto",
                }}
                className={`styled-description homepage-text-sm h-[100px]`}
                dangerouslySetInnerHTML={{
                  __html: caseData.marketingTexts?.description ?? "",
                }}
              ></motion.div>
              <button
                data-collapsed={collapsed}
                onClick={() => setCollapsed(!collapsed)}
                className="data-[collapsed=true]:absolute flex justify-center items-end data-[collapsed=false]:pt-4 data-[collapsed=true]:pt-10 to-70%  data-[collapsed=true]:bg-gradient-to-b from-transparent to-[var(--page-bg-color)] z-10 inset-x-0 bottom-0"
              >
                <FontAwesomeIcon
                  data-collapsed={collapsed}
                  icon={faChevronDown}
                  className="size-5 data-[collapsed=false]:rotate-180"
                />
              </button>
            </motion.div>
          </div>
          <div className="md:w-96 flex justify-end items-start">
            <SomeSharingLinks
              somelinks={
                cmsSection.specifics.somelinks ?? [
                  "linkedin",
                  "instagram",
                  "twitter",
                  "facebook",
                  "mail",
                ]
              }
              style={cmsSection.specifics.someStyle ?? "default"}
              encodedFullUrl={encodedFullUrl}
              caseData={caseData}
            />
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-4 md:gap-10">
          <div className="hidden md:flex overflow-hidden relative transition-all duration-300 ease-in-out flex-1">
            <motion.div className="flex flex-col">
              <div className="relative flex-col flex flex-1 overflow-hidden">
                <motion.div
                  id="contentSection"
                  animate={{ height: collapsed ? height : "auto" }}
                  style={{ height: `${height}px` }}
                  className={`styled-description homepage-text-sm`}
                  dangerouslySetInnerHTML={{
                    __html: caseData.marketingTexts?.description ?? <div></div>,
                  }}
                ></motion.div>
                {showExpand && (
                  <button
                    id="expand-text-btn"
                    data-collapsed={collapsed}
                    onClick={() => setCollapsed(!collapsed)}
                    className="data-[collapsed=true]:absolute flex justify-center items-end data-[collapsed=false]:pt-4 data-[collapsed=true]:pt-10 to-70%  data-[collapsed=true]:bg-gradient-to-b from-transparent to-[var(--page-bg-color)] z-10 inset-x-0 bottom-0"
                  >
                    <FontAwesomeIcon
                      data-collapsed={collapsed}
                      icon={faChevronDown}
                      className="size-5 data-[collapsed=false]:rotate-180"
                    />
                  </button>
                )}
              </div>
            </motion.div>
          </div>
          <div
            id="menuSection"
            className="w-full md:w-96 gap-4 flex flex-col flex-shrink-0"
          >
            <div className="grid grid-cols-1 gap-2">
              {caseData.isActive && caseData.documents?.length > 0 && (
                <RippleCtaButton
                  config={{
                    borderColor: "transparent",
                  }}
                  className="w-full justify-start"
                  onClick={(e) => {
                    if (!window) {
                      return;
                    }

                    window.dispatchEvent(
                      new CustomEvent("selectedTabEvent", {
                        detail: { selectedTab: "documents" },
                      })
                    );
                    e.preventDefault();
                    document
                      .getElementById("caseContactSection")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon className="size-5" icon={faFile} />
                    {caseData.isRental
                      ? "Hent lejemateriale"
                      : "Hent salgsmateriale"}
                  </span>
                </RippleCtaButton>
              )}
              {caseData.isActive && (
                <RippleCtaButton
                  config={{
                    borderColor: "transparent",
                  }}
                  className="w-full justify-start"
                  onClick={(e) => {
                    if (!window) {
                      return;
                    }

                    e.preventDefault();
                    window.dispatchEvent(
                      new CustomEvent("selectedTabEvent", {
                        detail: { selectedTab: "viewing" },
                      })
                    );
                    document
                      .getElementById("caseContactSection")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon className="size-5" icon={faCalendarDays} />
                    <span>Bestil fremvisning</span>
                  </span>
                </RippleCtaButton>
              )}
              {!cmsSection.specifics.hideAssessmentFormButton && (
                <Link
                  href={`${cmsSection.specifics?.assesmentFormSlug || "/saelgbolig"}#assessment-form`}
                >
                  <RippleCtaButton
                    config={{
                      borderColor: "transparent",
                    }}
                    className="w-full justify-start"
                  >
                    <span className="flex items-center gap-2">
                      <FontAwesomeIcon className="size-5" icon={faHome} />
                      <span>Bestil vurdering</span>
                    </span>
                  </RippleCtaButton>
                </Link>
              )}
              {caseData.isActive && caseData.canSendBid && (
                <RippleCtaButton
                  config={{
                    borderColor: "transparent",
                  }}
                  className="w-full justify-start"
                  onClick={(e) => {
                    if (!window) {
                      return;
                    }

                    e.preventDefault();
                    window.dispatchEvent(
                      new CustomEvent("selectedTabEvent", {
                        detail: { selectedTab: "bid" },
                      })
                    );
                    document
                      .getElementById("caseContactSection")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon
                      className="size-5"
                      icon={faHandHoldingDollar}
                    />
                    <span>Giv et bud</span>
                  </span>
                </RippleCtaButton>
              )}
            </div>
            <Card>
              <div
                style={{
                  backgroundColor: cmsSection.specifics?.card?.bgColor,
                  color: cmsSection.specifics?.card?.textColor,
                }}
              >
                <div className="p-4 pb-0 flex justify-between homepage-text-lg gap-2 items-center">
                  <div className="text-ellipsis whitespace-nowrap overflow-hidden">
                    {caseData?.estateType}
                  </div>
                  <div className="whitespace-nowrap">{caseData.priceInfo}</div>
                </div>
                <PropertyFacts caseData={caseData} />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </SectionLayout>
  );
};

export default CasePresentationSection;
